// =============================================================================
// Teams Sidebar - Navigation pour le module Équipes
// Onglets: Personnes, Équipes, Analyses
// =============================================================================

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  ChevronLeft,
  ChevronRight,
  Users,
  UserCircle,
  BarChart3,
  UsersRound,
  FolderOpen,
  Network,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTeamsStore } from '@/modules/teams/hooks/useTeamsStore';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface TeamsSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const NAV_ITEMS = [
  {
    path: '/teams',
    icon: UsersRound,
    label: 'Équipes',
    description: 'Groupes d\'équipes',
  },
  {
    path: '/teams/people',
    icon: UserCircle,
    label: 'Personnes',
    description: 'Tous les membres du workspace',
  },
  {
    path: '/teams/analytics',
    icon: BarChart3,
    label: 'Analyses',
    description: 'Statistiques et indicateurs',
  },
  {
    path: '/teams/orgchart',
    icon: Network,
    label: 'Organigramme',
    description: 'Hiérarchie de l\'organisation',
  },
];

const COLORS = {
  bg: '#FFFFFF',
  bgHover: '#FCE7F3',
  bgActive: '#FBCFE8',
  border: '#E2E8F0',
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  accent: '#EC4899', // Pink matching the module color
};

export function TeamsSidebar({ collapsed, onToggle }: TeamsSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const { teams, teamMembers } = useTeamsStore();

  // Filter teams the current user belongs to (teamMembers has user_id directly)
  const myTeams = user
    ? teams.filter(t => teamMembers.some(tm => tm.team_id === t.id && tm.user_id === user.id))
    : [];

  const isActive = (path: string) => {
    if (path === '/teams') {
      return location.pathname === '/teams';
    }
    return location.pathname === path;
  };

  const handleNavigation = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    navigate(path);
  };

  if (collapsed) {
    return (
      <aside
        className="flex flex-col w-12 shrink-0 border rounded-xl h-full"
        style={{ backgroundColor: COLORS.bg, borderColor: COLORS.border }}
      >
        <div
          className="h-14 flex items-center justify-center border-b"
          style={{ borderColor: COLORS.border }}
        >
          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8"
            style={{ color: COLORS.textSecondary }}
            onClick={onToggle}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <ScrollArea className="flex-1">
          <div className="flex flex-col items-center py-3 gap-1">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    <a
                      href={item.path}
                      onClick={(e) => handleNavigation(e, item.path)}
                      className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                      style={{
                        backgroundColor: active ? COLORS.bgActive : 'transparent',
                      }}
                      onMouseEnter={(e) => {
                        if (!active) e.currentTarget.style.backgroundColor = COLORS.bgHover;
                      }}
                      onMouseLeave={(e) => {
                        if (!active) e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Icon
                        className="h-4 w-4"
                        style={{
                          color: active ? COLORS.accent : COLORS.textSecondary,
                          strokeWidth: active ? 2.5 : 2,
                        }}
                      />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    style={{ backgroundColor: '#1E293B', color: '#FFFFFF', borderColor: '#334155' }}
                  >
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </ScrollArea>
      </aside>
    );
  }

  return (
    <aside
      className="flex flex-col w-56 shrink-0 border rounded-xl h-full"
      style={{ backgroundColor: COLORS.bg, borderColor: COLORS.border }}
    >
      <div
        className="h-14 flex items-center justify-between px-4 border-b"
        style={{ borderColor: COLORS.border }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Users className="h-4 w-4 shrink-0" style={{ color: COLORS.accent }} />
          <span className="font-semibold text-sm truncate" style={{ color: COLORS.textPrimary }}>
            Équipes
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="w-7 h-7 shrink-0"
          style={{ color: COLORS.textSecondary }}
          onClick={onToggle}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <nav className="p-2 space-y-1">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <a
                key={item.path}
                href={item.path}
                onClick={(e) => handleNavigation(e, item.path)}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer',
                  'hover:bg-[#F1F5F9]'
                )}
                style={{
                  backgroundColor: active ? COLORS.bgActive : 'transparent',
                  color: active ? COLORS.accent : COLORS.textPrimary,
                  fontWeight: active ? 500 : 400,
                }}
              >
                <Icon
                  className="h-4 w-4 shrink-0"
                  style={{
                    color: active ? COLORS.accent : COLORS.textSecondary,
                    strokeWidth: active ? 2.5 : 2,
                  }}
                />
                <span className="truncate">{item.label}</span>
              </a>
            );
          })}
        </nav>

        {/* Mes équipes */}
        <div className="px-3 pt-4 pb-2">
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: COLORS.textSecondary }}>
            Mes équipes
          </p>
          {myTeams.length === 0 ? (
            <div className="rounded-lg border border-dashed p-3 text-center" style={{ borderColor: COLORS.border }}>
              <div className="flex justify-center gap-1 mb-2">
                <FolderOpen className="h-4 w-4" style={{ color: '#F59E0B' }} />
              </div>
              <p className="text-[11px] leading-tight" style={{ color: COLORS.textSecondary }}>
                Lorsque vous aurez rejoint ou créé une équipe, elle apparaîtra ici.
              </p>
            </div>
          ) : (
            <div className="space-y-0.5">
              {myTeams.map(team => (
                <a
                  key={team.id}
                  href="/teams"
                  onClick={(e) => handleNavigation(e, '/teams')}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors hover:bg-[#F1F5F9] cursor-pointer"
                  style={{ color: COLORS.textPrimary }}
                >
                  <Avatar className="h-5 w-5">
                    <AvatarImage src={team.avatar_url || undefined} />
                    <AvatarFallback
                      className="text-[9px] font-medium text-white"
                      style={{ backgroundColor: team.color || COLORS.accent }}
                    >
                      {team.name.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate">{team.name}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>

      <div className="p-3 border-t" style={{ borderColor: COLORS.border }}>
        <p className="text-xs truncate" style={{ color: COLORS.textSecondary }}>
          Personnes, équipes et analyses
        </p>
      </div>
    </aside>
  );
}
